import * as utils from '@/common/utils'
import ApiService from '@/common/api.service'

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function properties () {
  return {
    id: null,
    client_id: null,
    name: null,
    latitude: null,
    longitude: null,
    zoom_level: null,
    icon: null,
    color: null,
    by_user_id: null,
    by_full_name: null,
    created_at: null,
    updated_at: null,
    poiWidgetMode: null,
    stepNo: 1,
    totalSteps: 1,
    transparency: 50
  }
}

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function defaults () {
  return {
    ...properties(),
    created_at: null,
    updated_at: null
  }
}

function formDefaults () {
  return {
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param poi Object
 *
 * @return Object
 */
function transformCreate (poi) {
  return {
    id: poi.id,
    client_id: poi.client_id,
    name: poi.name,
    latitude: poi.latitude,
    longitude: poi.longitude,
    zoom_level: poi.zoom_level,
    icon: poi.icon,
    color: poi.color
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param poi Object
 *
 * @return Object
 */
function transformUpdate (poi) {
  return {
    id: poi.id,
    client_id: poi.client_id,
    name: poi.name,
    latitude: poi.latitude,
    longitude: poi.longitude,
    zoom_level: poi.zoom_level,
    icon: poi.icon,
    color: poi.color
  }
}

/**
 * The module's state (VueX store object)
 */
export const state = {
  data: Object.assign({}, defaults()),
  form: Object.assign({}, formDefaults()),
  original: properties(),
  isModified: false,
  isLoading: false
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state.data
  },

  /**
   * Returns the form's state object
   *
   * @return Object
   */
  getForm (state) {
    return state.form
  },

  /**
   * Returns true if the primary data have been modified
   * since it was originally loaded.
   *
   * @return Boolean
   */
  isModified (state) {
    return state.isModified
  },

  /**
   * Returns true if the data for the module is still being loaded.
   *
   * @return Boolean
   */
  isLoading (state) {
    return state.isLoading
  }
}

export const actions = {
  /**
   * Request a record from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetch (context, id) {
    return ApiService
      .get(`client/poi/${id}`)
      .then((response) => {
        context.commit('set', response.data.data)
        context.commit('setIsModified', false)
        return response
      })
  },

  post (context) {
    return ApiService
      .post(`client/poi`, transformCreate(context.getters['get']))
      .then((response) => {
        context.commit('setProp', { name: 'id', value: response.data.data.id })
        return response
      })
  },

  put (context) {
    const id = context.getters['get'].id
    return ApiService
      .put(`client/poi/${id}`, transformUpdate(context.getters['get']))
      .then((response) => {
        return response
      })
  },

  delete (context) {
    const id = context.getters['get'].id
    return ApiService
      .delete(`client/poi/${id}`)
      .then((response) => {
        return response
      })
  }
}

export const mutations = {
  /**
   * Set all the data properties according to the provided data.
   *
   * @param state Object
   * @param data  Object
   */
  set (state, data) {
    state.data = Object.assign(defaults(), data)
  },

  setFormData (state, data) {
    state.form = {}
  },

  newPoi (state) {
    state.data.latitude = null
    state.data.longitude = null
  },

  editPoi (state, poi) {
    state.data.id = poi.id
    state.data.client_id = poi.client_id
    state.data.name = poi.name
    state.data.latitude = poi.latitude
    state.data.longitude = poi.longitude
    state.data.zoom_level = poi.zoom_level
    state.data.icon = poi.icon
    state.data.color = poi.color
  },

  /**
   * Sets the "modified" status to true or false
   *
   * @param state Object
   * @param isModified Boolean
   */
  setIsModified (state, isModified) {
    state.isModified = isModified
  },

  /**
   * Sets the "isLoading" status to true or false
   *
   * @param state Object
   * @param isLoading Boolean
   */
  setIsLoading (state, isLoading) {
    state.isLoading = isLoading
  },

  /**
   * Sets the original primary data.
   *
   * @param state Object
   * @param data  Object
   */
  setOriginal (state, data) {
    state.original = Object.assign(defaults(), data)
  },

  /**
   * Sets a specific property and value
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value String
   */
  setProp (state, { name, value }) {
    state.isModified = true
    utils.mutateProp(state.data, name, value)
  },

  /**
   * Pushes a new element to an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value mixed  The variable to push onto the array.
   */
  pushProp (state, { name, value }) {
    state.isModified = true
    state.data[name].push(value)
  },

  /**
   * Sets the values for multiple properties.
   *
   * @param state      Object
   * @param properties Array of objects in the form { name: 'propName', value: 'xyz' }
   */
  setProps (state, properties) {
    state.isModified = true
    properties.forEach(({ name, value }) => {
      utils.mutateProp(state.data, name, value)
    })
  },

  /**
   * removes an element from an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param index Number The index of the element to remove
   */
  pullProp (state, { name, index }) {
    utils.pullProp(state.data, name, index)
  },

  /**
   * removes an element from an array of a specific property
   * and replaced it with a new element.
   *
   * @param state   Object
   * @param name    String Name of property
   * @param index   Number The index of the element to remove
   * @param replacement New element to replace the old
   */
  spliceProp (state, { name, index, replacement }) {
    state.isModified = true
    state.data[name].splice(index, 1, replacement)
  },

  /**
   * Sets the zone categories in form.
   *
   * @param state       Object
   * @param categories Array of objects in the form { id: 1, name: 'xyz' }
   */
  // setZoneCategories (state, categories) {
  //   state.form.zone_categories = categories
  // },

  /**
   * Resets the state.
   *
   * @param state Object
   */
  reset (state) {
    state.data = defaults()
    state.original = properties()
    state.isModified = false
    state.isLoading = false
  },

  /**
   * Resets the form's state.
   *
   * @param state Object
   */
  resetForm (state) {
    state.form = formDefaults()
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/poi/clientPoiModule.js